import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElements } from "core-integration/src/store/elements/reducers";
import { IElement } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { proposeElement } from "core-integration/src/store/squad/thunks";
import orderBy from "lodash/orderBy";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass";
import styled, { css } from "styled-components";
import Logo from "../../img/logo.svg?react";
import { integerToDecimal } from "../../utils/decimal";
import { getGroupedElements } from "../../utils/elements";
import { getNextElementFixtures, getOpponentTeam } from "../../utils/fixtures";
import { integerDivide } from "../../utils/points";
import Badge from "../Badge";
import Button from "../Button";
import Copy from "../Copy";
import Dialog from "../Dialog";
import {
  ElementCardBadge,
  ElementCardMain,
  ElementCardTeam,
  ShirtWrap,
} from "../ElementCard";
import { PitchElementInner, PitchElementOuter } from "../Pitch";
import { ElementName, StyledPitchElementData } from "../PitchElementData";
import Shirt from "../Shirt";
import SubHeading from "../SubHeading";
import TeamCode from "../TeamCode";

const StyledParagraph = styled.p`
  margin: 0;
`;

const Header = styled.div<{ $elementTypeId: number }>`
  ${(props) =>
    props.$elementTypeId &&
    css`
      background-image: linear-gradient(
        to right,
        ${props.theme.colors.elementTypes[props.$elementTypeId].colors[0]} 75%,
        ${props.theme.colors.elementTypes[props.$elementTypeId].colors[1]}
      );
      color: ${props.theme.colors.white};
    `};
  flex: 1;
  border-start-end-radius: ${(props) => props.theme.radii[1]};
  border-start-start-radius: ${(props) => props.theme.radii[1]};
`;

const ElementsWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding: ${(props) => props.theme.space[1]} 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  flex: 1;
  margin: 0;
  padding: ${(props) => `${props.theme.space[1]}`};
  font-size: ${(props) => props.theme.fontSizes[3]};
  text-transform: capitalize;
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.space[2]};
  margin-bottom: ${(props) => props.theme.space[2]};
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-direction: row;
  }
`;

const StyledPitchElementOuter = styled(PitchElementOuter)`
  padding: 0;
  width: 10.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 11.7rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 15.5rem;
  }
`;

const CardWrap = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: ${(props) => props.theme.space[1]};
`;

const CardSelectBackground = styled.div<{ $isSelected: boolean }>`
  display: flex;
  padding: ${(props) => props.theme.space[1]};
  background-color: transparent;
  border-radius: ${(props) => props.theme.radii[1]};
  cursor: pointer;

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      background-color: ${theme.colors.yellow};
    `}
`;

const StyledElementName = styled(ElementName)`
  text-align: center;
`;

const Score = styled.div<{ elementTypeId: number }>`
  padding: ${(props) => props.theme.space[1]};
  background-image: linear-gradient(
    to right,
    ${(props) => props.theme.colors.elementTypes[props.elementTypeId].colors[0]}
      75%,
    ${(props) => props.theme.colors.elementTypes[props.elementTypeId].colors[1]}
  );
  border-bottom-right-radius: ${(props) => props.theme.radii[1]};
  border-bottom-left-radius: ${(props) => props.theme.radii[1]};
  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.body};
  text-align: center;
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${(props) => props.theme.space[1]};
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0.96;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
`;

const Stat = styled.div<{ $elementTypeId: number }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${(props) => props.theme.space[1]};

  ${(props) =>
    props.$elementTypeId &&
    css`
      border-inline-end: 1.5px solid
        ${props.theme.colors.elementTypes[props.$elementTypeId].colors[1]}50;
    `};
  &:last-child {
    border: none;
  }
`;

const StatTitle = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.offBlack};
  flex: 1;
  align-content: center;
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    font-size: ${(props) => props.theme.fontSizes[0]};
  }
`;
const StatValue = styled.span`
  font-size: ${(props) => props.theme.fontSizes[0]};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.offBlack};
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    font-size: ${(props) => props.theme.fontSizes[1]};
  }
`;

const NextFixtureContainer = styled.div<{ $elementTypeId: number }>`
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: center;
  padding: ${(props) => props.theme.space[1]};
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0.96;
  border-bottom-right-radius: ${(props) => props.theme.radii[1]};
  border-bottom-left-radius: ${(props) => props.theme.radii[1]};

  ${(props) =>
    props.$elementTypeId &&
    css`
      border-inline-end: 1.5px solid
        ${props.theme.colors.elementTypes[props.$elementTypeId].colors[1]}50;
    `};
  &:last-child {
    border: none;
  }
`;

const FixtureTitle = styled.div<{ $elementTypeId: number }>`
  flex: 1;
  color: ${({ theme }) => theme.colors.offBlack};
  font-size: 1.2rem;

  ${(props) =>
    props.$elementTypeId &&
    css`
      border-inline-end: 1.5px solid
        ${props.theme.colors.elementTypes[props.$elementTypeId].colors[1]}50;
    `};
  &:last-child {
    border: none;
  }
`;
const FixtureBadgeWrap = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.offBlack};
  font-size: ${(props) => props.theme.fontSizes[1]};
`;

const BadgePlaceholder = styled.div`
  align-content: center;
  height: 25px; // same as badge size to keep uniform height
  color: ${({ theme }) => theme.colors.offBlack};
  font-size: ${(props) => props.theme.fontSizes[1]};
  font-family: ${(props) => props.theme.fonts.body};
`;

const DialogHeader = styled.div`
  display: flex;
  gap: ${(props) => props.theme.space[2]};
  align-items: center;
  padding: ${(props) =>
    `${props.theme.space[3]} ${props.theme.space[2]} ${props.theme.space[2]}`};
  border-top-left-radius: ${(props) => props.theme.radii[1]};
  border-top-right-radius: ${(props) => props.theme.radii[1]};
`;

const DialogHeading = styled.h2`
  flex: 1;
  margin: 0;
  color: ${(props) => props.theme.colors.charcoal};
  font-family: ${(props) => props.theme.fonts.body};
  font-weight: 500;
  font-size: 2rem;
`;

const CloseButton = styled.span`
  border: 0;
  border-radius: ${(props) => props.theme.radii[0]};
  text-decoration: underline;
  font-weight: 600;
  font-size: ${(props) => props.theme.fontSizes[1]};
  cursor: pointer;
`;

const DialogContent = styled.div`
  padding: ${(props) => props.theme.space[1]};
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    padding: ${(props) => props.theme.space[2]};
  }
`;

interface IAutoPickDialogProps {
  handleHideAutoPickDialog: () => void;
  handleAutoPick: (config: {
    action: () => void;
    interactionText: string;
  }) => void;
  autoPick: () => void;
}

const AutoPickDialog = ({
  handleHideAutoPickDialog,
  handleAutoPick,
  autoPick,
}: IAutoPickDialogProps) => {
  const [selectedElementId, setSelectedElementId] = useState<number | null>(
    null,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();

  const buttonsRef = useRef<HTMLDivElement | null>(null);
  const elements = useSelector((state: RootState) => getElements(state));
  const fixturesByEvent = useSelector(getFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);

  const groupedElements = getGroupedElements(elements);
  const frontCourtElements = groupedElements.front_court;
  const backCourtElements = groupedElements.back_court;

  const autoPickText = t("createSquad.onboarding.autoPickn", "Auto Pick");
  const userPickText = t(
    "createSquad.onboarding.entryPickn",
    "I'll Pick the Rest of My Squad!",
  );

  useEffect(() => {
    if (!selectedElementId || !buttonsRef.current) return;

    requestAnimationFrame(() => {
      buttonsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      buttonsRef.current
        ?.querySelector("button")
        ?.focus({ preventScroll: true });
    });
  }, [selectedElementId]);

  const renderNextFixture = (element: IElement) => {
    const neededFixtures = 1;
    const elementFixtures = getNextElementFixtures(
      nextEvent,
      neededFixtures,
      fixturesByEvent,
      element,
    );

    const nextFixture = elementFixtures[0];
    return nextEvent && nextFixture?.code ? (
      <Badge
        teamId={getOpponentTeam(element.team, nextFixture)}
        width={"25px"}
      />
    ) : (
      <BadgePlaceholder> - </BadgePlaceholder>
    );
  };

  const renderElementCards = (elements: IElement[]) => {
    const headerTitle =
      elements[2].element_type === 2 ? "Front Court" : "Back Court";
    return (
      <Container>
        <Header $elementTypeId={elements[1].element_type}>
          <Title>{headerTitle}</Title>
        </Header>
        <ElementsWrap>
          {orderBy(elements, ["now_cost", "total_points"], ["desc", "desc"])
            .slice(0, 3)
            .map((element: IElement) => (
              <CardSelectBackground
                key={element.id}
                $isSelected={selectedElementId === element.id}
              >
                <CardWrap
                  onClick={() => setSelectedElementId(element.id)}
                  $isSelected={selectedElementId === element.id}
                >
                  <StyledPitchElementOuter>
                    <PitchElementInner>
                      <ElementCardMain>
                        <ElementCardTeam>
                          <ElementCardBadge>
                            <Badge
                              teamId={element.team}
                              isPresentation={true}
                            />
                          </ElementCardBadge>
                          <Box mr={1}>
                            <TeamCode teamId={element.team} />
                          </Box>
                        </ElementCardTeam>
                        <ShirtWrap>
                          <Shirt elementId={element.id} />
                        </ShirtWrap>
                        <StyledPitchElementData
                          elementTypeId={element.element_type}
                        >
                          <StyledElementName>
                            {Array.from(element.first_name)[0]}.
                            <strong>{element.second_name}</strong>
                          </StyledElementName>
                        </StyledPitchElementData>
                        <Score elementTypeId={element.element_type}>
                          <StatsWrapper>
                            <StatsContainer>
                              <Stat $elementTypeId={element.element_type}>
                                <StatTitle>
                                  {t(
                                    "createSquad.onboarding.pointsScored",
                                    "Points",
                                  )}
                                </StatTitle>
                                <StatValue>
                                  {integerDivide(element.total_points, 10)}
                                </StatValue>
                              </Stat>
                              <Stat $elementTypeId={element.element_type}>
                                <StatTitle>
                                  {t("stats.now_cost.labeln", "Cost")}
                                </StatTitle>
                                <StatValue>
                                  ${integerToDecimal(element.now_cost, 10)}
                                </StatValue>
                              </Stat>
                            </StatsContainer>
                            <NextFixtureContainer
                              $elementTypeId={element.element_type}
                            >
                              <FixtureTitle
                                $elementTypeId={element.element_type}
                              >
                                {t("fixtures.next", "Next")}
                              </FixtureTitle>
                              <FixtureBadgeWrap>
                                {renderNextFixture(element)}
                              </FixtureBadgeWrap>
                            </NextFixtureContainer>
                          </StatsWrapper>
                        </Score>
                      </ElementCardMain>
                    </PitchElementInner>
                  </StyledPitchElementOuter>
                </CardWrap>
              </CardSelectBackground>
            ))}
        </ElementsWrap>
      </Container>
    );
  };

  return (
    <Dialog closeDialog={handleHideAutoPickDialog}>
      <DialogContent>
        <DialogHeader>
          <Logo />
          <DialogHeading id="ism-dialog-title">
            {t("createSquad.onboarding.titlen", "Welcome to NBA Fantasy")}
          </DialogHeading>
          <CloseButton onClick={handleHideAutoPickDialog}>
            {t("createSquad.onboarding.skip", "Skip")}
          </CloseButton>
        </DialogHeader>
        <Dialog.Body>
          <Box mb={3}>
            <SubHeading>
              {t("createSquad.onboarding.subHeading", "Pick Your Squad")}
            </SubHeading>
            <StyledParagraph>
              {t(
                "createSquad.onboarding.p1n",
                "Select 1 star to build your team around. You can change your line-up afterwards.",
              )}
            </StyledParagraph>
          </Box>

          <Box mb={2}>{renderElementCards(frontCourtElements)}</Box>
          <Box mb={2}>{renderElementCards(backCourtElements)}</Box>

          {selectedElementId && (
            <div ref={buttonsRef}>
              <Copy>
                <p>
                  {t(
                    "createSquad.onboarding.p2n",
                    "Now you've selected your star player, complete the rest of your team below.",
                  )}
                </p>
              </Copy>
              <ButtonWrap>
                <Button
                  onClick={() => {
                    dispatch(proposeElement(selectedElementId));
                    handleAutoPick({
                      action: handleHideAutoPickDialog,
                      interactionText: userPickText,
                    });
                  }}
                  fullwidth="true"
                >
                  {userPickText}
                </Button>
                <Button
                  onClick={() => {
                    handleAutoPick({
                      action: () => {
                        dispatch(proposeElement(selectedElementId));
                        autoPick();
                        handleHideAutoPickDialog();
                      },
                      interactionText: autoPickText,
                    });
                  }}
                  fullwidth="true"
                >
                  {autoPickText}
                </Button>
              </ButtonWrap>
            </div>
          )}
        </Dialog.Body>
      </DialogContent>
    </Dialog>
  );
};

export default AutoPickDialog;
